import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Spinner } from "../../components/common/Spinner";
import ClientJourneyProgressBar from "../../components/ui/ClientJourneyProgressBar";
import {
  fetchJourneyTemplates,
  fetchClientJourneyById,
  assignJourneyToClient,
  updateClientJourneyProgress,
} from "../../store/thunks/clientJourneyThunk";
import { clearCurrentJourney } from "../../store/slices/clientJourneySlice";

const ClientJourney = ({ clientId }) => {
  const dispatch = useDispatch();
  const { currentJourney, status, error } = useSelector((state) => state.clientJourney);
  const journeysState = useSelector((state) => state.journeys || {});
  const { journeyTemplates = [], status: journeyStatus } = journeysState;

  // Memoize selectors to prevent unnecessary re-renders
  const isLoading = status === "loading" || journeyStatus === "loading";
  const hasError = status === "failed" || journeyStatus === "failed";

  // Load available journeys and client's current journey
  useEffect(() => {
    const loadJourneys = async () => {
      try {
        await dispatch(fetchJourneyTemplates()).unwrap();
        await dispatch(fetchClientJourneyById(clientId)).unwrap();
      } catch (error) {
        console.error("Error loading journeys:", error);
        toast.error("Failed to load journeys");
      }
    };

    loadJourneys();
  }, [dispatch, clientId]);

  const handleAssignJourney = useCallback(async (journeyTemplateId) => {
    try {
      await dispatch(
        assignJourneyToClient({
          clientId,
          journeyTemplateId,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error assigning journey:", error);
      toast.error("Failed to assign journey");
    }
  }, [dispatch, clientId]);

  const handleUpdateProgress = useCallback(async (progressData) => {
    try {
      await dispatch(
        updateClientJourneyProgress({
          clientId,
          progressData,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error updating progress:", error);
      toast.error("Failed to update progress");
    }
  }, [dispatch, clientId]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="text-red-500 text-center p-4">
        {error || "Failed to load journeys"}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Journey Progress */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Current Journey</h3>
        {currentJourney?.journeyTemplateId ? (
          <div className="space-y-4">
            <div className="text-sm text-gray-500 mb-4">
              Started: {new Date(currentJourney.startedAt).toLocaleDateString()}
            </div>

            {/* Journey Progress Bar */}
            <div className="pb-8">
              <ClientJourneyProgressBar
                currentStep={currentJourney.currentStepIndex}
                isLooping={currentJourney.loopCount > 1}
                loopCount={currentJourney.loopCount}
                sections={currentJourney.journeyTemplateId.sections.map(section => ({
                  name: section.name,
                  color: section.color,
                  substeps: section.steps
                }))}
                clientId={clientId}
              />
            </div>
          </div>
        ) : (
          <p className="text-gray-500">No journey assigned yet</p>
        )}
      </div>

      {/* Available Journeys */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Available Journeys</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.isArray(journeyTemplates) && journeyTemplates.length > 0 ? journeyTemplates.map((template) => (
            <div
              key={template._id}
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                currentJourney?.journeyTemplateId?._id === template._id
                  ? "bg-blue-50 border-blue-200"
                  : "hover:bg-gray-50 border-gray-200"
              }`}
              onClick={() => {
                if (currentJourney?.journeyTemplateId?._id !== template._id) {
                  handleAssignJourney(template._id);
                }
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium">{template.name}</h4>
                {currentJourney?.journeyTemplateId?._id === template._id && (
                  <span className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
                    Current
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">{template.description}</p>
              <div className="mt-2 text-sm text-gray-500">
                {template.sections?.length || 0} sections
              </div>
            </div>
          )) : (
            <div className="col-span-full text-center text-gray-500 py-8">
              {isLoading ? "Loading journey templates..." : "No journey templates available"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientJourney;